import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from 'semantic-ui-react';
import { useShare } from '../../context/ShareContext';
import { useCreateShareBundleMutation } from '../../redux/apis/endpoints/shareBundleApi';
import './ShareBundleModal.css';

const SOURCE_LABELS = {
  selection: 'these items',
  closet: 'this closet',
  lookbook: 'this lookbook',
  vto_result: 'this look',
  profile: 'your profile'
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Safari/older fallback
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

/**
 * The share sheet for anything bigger than a single try-on: a multi-select of
 * clothes, a set of looks, a whole closet, or a whole lookbook.
 *
 * Driven entirely by ShareContext, so it is mounted once at the app root.
 */
const ShareBundleModal = () => {
  const { shareRequest, closeShare } = useShare();
  const [createShareBundle, { isLoading }] = useCreateShareBundleMutation();

  const [includeLooks, setIncludeLooks] = useState(true);
  const [shareUrl, setShareUrl] = useState(null);
  const [serverCounts, setServerCounts] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const isOpen = !!shareRequest;

  // Reset per-share state whenever a new share is opened
  useEffect(() => {
    if (isOpen) {
      setIncludeLooks(true);
      setShareUrl(null);
      setServerCounts(null);
      setCopied(false);
      setError(null);
    }
  }, [isOpen, shareRequest]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeShare();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeShare]);

  const previews = useMemo(
    () => (shareRequest?.previewImages || []).filter(Boolean).slice(0, 6),
    [shareRequest]
  );

  // A closet/lookbook share resolves live on the server, so the card's cached
  // count is only an estimate — once the link exists, use the real numbers.
  const itemCount = useMemo(() => {
    if (serverCounts) return (serverCounts.clothes || 0) + (serverCounts.looks || 0);
    if (!shareRequest) return 0;
    if (typeof shareRequest.itemCount === 'number') return shareRequest.itemCount;
    return (shareRequest.clothesIds?.length || 0) + (shareRequest.vtoResultIds?.length || 0);
  }, [shareRequest, serverCounts]);

  const isLiveFolder =
    shareRequest?.sourceType === 'closet' || shareRequest?.sourceType === 'lookbook';
  const isProfile = shareRequest?.sourceType === 'profile';

  // A lookbook is nothing but looks — the toggle would let you share an empty bundle
  const canToggleLooks =
    shareRequest?.sourceType === 'selection' &&
    (shareRequest?.vtoResultIds?.length || 0) > 0 &&
    (shareRequest?.clothesIds?.length || 0) > 0;

  const handleCreateLink = useCallback(async () => {
    if (!shareRequest || isLoading) return;

    setError(null);
    try {
      const result = await createShareBundle({
        sourceType: shareRequest.sourceType,
        closetId: shareRequest.closetId,
        lookbookId: shareRequest.lookbookId,
        clothesIds: shareRequest.clothesIds || [],
        vtoResultIds: shareRequest.vtoResultIds || [],
        includeVtoResults: canToggleLooks ? includeLooks : true,
        title: shareRequest.title
      }).unwrap();

      setShareUrl(result.shareUrl);
      setServerCounts(result.counts);
      await copyToClipboard(result.shareUrl);
      setCopied(true);
    } catch (err) {
      console.error('[Share] Failed to create share link:', err);
      setError(err?.data?.message || err?.message || 'Could not create the link. Try again.');
    }
  }, [shareRequest, isLoading, createShareBundle, canToggleLooks, includeLooks]);

  const handleCopyAgain = useCallback(async () => {
    if (!shareUrl) return;
    await copyToClipboard(shareUrl);
    setCopied(true);
  }, [shareUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!shareUrl || !navigator.share) return;
    try {
      await navigator.share({
        title: shareRequest?.title || 'My SUSU closet',
        text: `Take a look at ${SOURCE_LABELS[shareRequest?.sourceType] || 'my items'} on SUSU Closet`,
        url: shareUrl
      });
    } catch (err) {
      // User dismissed the OS sheet — nothing to report
      if (err?.name !== 'AbortError') console.warn('[Share] Native share failed:', err);
    }
  }, [shareUrl, shareRequest]);

  if (!isOpen) return null;

  const subject = SOURCE_LABELS[shareRequest.sourceType] || 'these items';

  return createPortal(
    <div className="share-bundle-overlay" onClick={closeShare}>
      <div
        className="share-bundle-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Share"
      >
        <div className="share-bundle-header">
          <div>
            <div className="share-bundle-title">
              {shareUrl ? 'Link ready' : `Share ${subject}`}
            </div>
            <div className="share-bundle-subtitle">
              {isProfile
                ? shareUrl
                  ? 'Anyone with this link can see your profile'
                  : 'Create a link that opens your profile'
                : shareUrl
                  ? 'Anyone with this link can add these to their own closet'
                  : 'Create a link that adds these straight to a friend’s closet'}
            </div>
          </div>
          <button className="share-bundle-close" onClick={closeShare} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* What's in the bundle */}
        <div className="share-bundle-preview">
          {previews.length > 0 ? (
            <div className="share-bundle-thumbs">
              {previews.map((url, idx) => (
                <div className="share-bundle-thumb" key={idx}>
                  <img src={url} alt="" />
                </div>
              ))}
              {itemCount > previews.length && (
                <div className="share-bundle-thumb share-bundle-thumb-more">
                  +{itemCount - previews.length}
                </div>
              )}
            </div>
          ) : (
            <div className="share-bundle-thumbs-empty">
              <Icon name="images outline" />
            </div>
          )}
          <div className="share-bundle-count">
            {shareRequest.title ? <strong>{shareRequest.title}</strong> : null}
            {shareRequest.title ? ' · ' : null}
            {isProfile
              ? 'Your closets and lookbooks, unless your account is private'
              : serverCounts || !isLiveFolder
                ? itemCount > 0
                  ? `${itemCount} item${itemCount === 1 ? '' : 's'}`
                  : 'Everything in it'
                : 'Everything in it right now'}
          </div>
        </div>

        {/* Include-looks toggle, only where it changes the outcome */}
        {canToggleLooks && !shareUrl && (
          <button
            className={`share-bundle-toggle ${includeLooks ? 'on' : ''}`}
            onClick={() => setIncludeLooks((v) => !v)}
            type="button"
          >
            <span className="share-bundle-toggle-track">
              <span className="share-bundle-toggle-knob" />
            </span>
            <span>
              <span className="share-bundle-toggle-label">Include try-on results</span>
              <span className="share-bundle-toggle-desc">
                Adds the finished look images to their clothing panel too
              </span>
            </span>
          </button>
        )}

        {error && <div className="share-bundle-error">{error}</div>}

        {/* Actions */}
        {!shareUrl ? (
          <button
            className="share-bundle-primary"
            onClick={handleCreateLink}
            disabled={isLoading}
          >
            {isLoading ? <Icon name="spinner" loading /> : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7.5 10.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M10.5 7.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            )}
            {isLoading ? 'Creating link…' : 'Create share link'}
          </button>
        ) : (
          <>
            <div className="share-bundle-url" onClick={handleCopyAgain} role="button" title="Copy again">
              <span className="share-bundle-url-text">{shareUrl}</span>
              <Icon name={copied ? 'check' : 'copy outline'} />
            </div>
            <div className="share-bundle-actions">
              <button className="share-bundle-primary" onClick={handleCopyAgain}>
                {copied ? 'Copied to clipboard' : 'Copy link'}
              </button>
              {typeof navigator !== 'undefined' && navigator.share && (
                <button className="share-bundle-secondary" onClick={handleNativeShare}>
                  <Icon name="share" /> Share…
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ShareBundleModal;
